import { ChainId } from '@nominex/nomiswap-sdk'
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import BigNumber from 'bignumber.js'
import { getPoolsConfig, SerializedPoolConfig } from 'config/pools'
import type { AppState } from 'state'
import { AppThunk, PoolsState, SerializedPool } from 'state/types'
import { fetchHolderBonusPools } from 'state/farms/utils'
import { getPoolApr } from 'utils/apr'
import { getBalanceNumber } from 'utils/formatBalance'
import { addressesEqual } from 'utils/addressHelpers'
import { fetchLaunchpoolsTotalStaking, fetchPoolsBoostingInfo, fetchPoolsTotalStaking } from './fetchPools'
import {
  fetchPoolsAllowance,
  fetchUserBalances,
  fetchUserBoostingInfo,
  fetchUserPendingRewards,
  fetchUserStakeBalances,
} from './fetchPoolsUser'
import { getTokenPricesFromFarm } from './helpers'
import { fetchPoolsAllocation } from './fetchPoolsAllocation'

const initialState: PoolsState = {
  data: [],
  userDataLoaded: false,
}

// Thunks
export const fetchInitialPoolsData = createAsyncThunk<
  { data: SerializedPoolConfig[] },
  { chainId: ChainId },
  { state: AppState }
>('farms/fetchInitialPoolsData', ({ chainId }) => ({
  data: [...getPoolsConfig(chainId)],
  userDataLoaded: false,
}))

export const fetchPoolsPublicDataAsync = (chainId: ChainId) => async (dispatch, getState) => {
  const [totalStakings, tokenPerSecByPools, boostingInfos, holderBonusPools, launchpoolsTotalStakings] =
    await Promise.all([
      fetchPoolsTotalStaking(chainId),
      fetchPoolsAllocation(chainId),
      fetchPoolsBoostingInfo(chainId),
      fetchHolderBonusPools(),
      fetchLaunchpoolsTotalStaking(chainId),
    ])

  const prices = getTokenPricesFromFarm(getState().farms.data)
  const poolsConfig = getPoolsConfig(chainId)

  const liveData = poolsConfig.map(
    ({ contractAddress, earningToken, isPrivate, isFinished, launchpoolAddress, sousId, stakingToken }) => {
      const totalStaking = totalStakings.find((pool) => pool.sousId === sousId)
      const { tokensPerSec } = tokenPerSecByPools.find((pool) => pool.sousId === sousId)
      const boostingInfo = boostingInfos.find((pool) => pool.sousId === sousId)?.info
      const holderBonusForPool = holderBonusPools.find(({ farmAddress }) =>
        addressesEqual(contractAddress, farmAddress),
      )

      const stakingTokenPrice = prices[stakingToken.address.toLowerCase()] || 0
      const earningTokenPrice = prices[earningToken.address.toLowerCase()] || 0
      const launchpoolTotalStaked = launchpoolsTotalStakings.find(({ address }) =>
        addressesEqual(address, launchpoolAddress),
      ).totalStaked

      const aprFactor = isPrivate
        ? new BigNumber(boostingInfo?.totalShares)
            .div(new BigNumber(boostingInfo?.totalShares).plus(boostingInfo?.unstakedShares))
            .toNumber()
        : 1

      const apr =
        aprFactor *
        getPoolApr(
          stakingTokenPrice,
          earningTokenPrice,
          getBalanceNumber(new BigNumber(launchpoolTotalStaked), stakingToken.decimals),
          tokensPerSec,
        )

      return {
        ...totalStaking,
        ...(!!holderBonusForPool && {
          holderBonusPool: {
            effectivePoolSizeInLpTokens: holderBonusForPool.effectivePoolSizeInLpTokens,
            dailyPoolSizeNmx: holderBonusForPool.dailyPoolSizeNmx,
          },
        }),
        ...(!!boostingInfo && { boostingInfo }),
        stakingTokenPrice,
        earningTokenPrice,
        apr,
        isFinished,
      }
    },
  )

  dispatch(setPoolsPublicData(liveData))
}

export const fetchPoolsUserDataAsync =
  (account: string, chainId: ChainId): AppThunk =>
  async (dispatch) => {
    dispatch(setUserDataLoading())

    const [allowances, stakingTokenBalances, stakedBalances, pendingRewards, boostingInfos] = await Promise.all([
      fetchPoolsAllowance(account, chainId),
      fetchUserBalances(account, chainId),
      fetchUserStakeBalances(account, chainId),
      fetchUserPendingRewards(account, chainId),
      fetchUserBoostingInfo(account, chainId),
    ])

    const poolsConfig = getPoolsConfig(chainId)
    const userData = poolsConfig.map(({ sousId }) => ({
      sousId,
      allowance: allowances[sousId],
      stakingTokenBalance: stakingTokenBalances[sousId],
      stakedBalance: stakedBalances[sousId],
      pendingReward: pendingRewards[sousId],
      ...(!!boostingInfos[sousId] && { boostingInfo: boostingInfos[sousId] }),
    }))

    dispatch(setPoolsUserData(userData))
  }

export const updateUserAllowance =
  (sousId: number, account: string, chainId: ChainId): AppThunk =>
  async (dispatch) => {
    const allowances = await fetchPoolsAllowance(account, chainId)
    dispatch(updatePoolsUserData({ sousId, field: 'allowance', value: allowances[sousId] }))
  }

export const updateUserBalance =
  (sousId: number, account: string, chainId: ChainId): AppThunk =>
  async (dispatch) => {
    const tokenBalances = await fetchUserBalances(account, chainId)
    dispatch(updatePoolsUserData({ sousId, field: 'stakingTokenBalance', value: tokenBalances[sousId] }))
  }

export const updateUserStakedBalance =
  (sousId: number, account: string, chainId: ChainId): AppThunk =>
  async (dispatch) => {
    const stakedBalances = await fetchUserStakeBalances(account, chainId)
    dispatch(updatePoolsUserData({ sousId, field: 'stakedBalance', value: stakedBalances[sousId] }))
  }

export const updateUserPendingReward =
  (sousId: number, account: string, chainId: ChainId): AppThunk =>
  async (dispatch) => {
    const pendingRewards = await fetchUserPendingRewards(account, chainId)
    dispatch(updatePoolsUserData({ sousId, field: 'pendingReward', value: pendingRewards[sousId] }))
  }

export const PoolsSlice = createSlice({
  name: 'Pools',
  initialState,
  reducers: {
    setPoolsPublicData: (state, action) => {
      const livePoolsData: SerializedPool[] = action.payload
      state.data = state.data.map((pool) => {
        const livePoolData = livePoolsData.find(({ sousId }) => sousId === pool.sousId)
        return { ...pool, ...livePoolData }
      })
    },
    setUserDataLoading: (state) => {
      state.userDataLoaded = false
    },
    setPoolsUserData: (state, action) => {
      const userData = action.payload
      state.data = state.data.map((pool) => {
        const userPoolData = userData.find(({ sousId }) => sousId === pool.sousId)
        return { ...pool, userData: userPoolData }
      })
      state.userDataLoaded = true
    },
    updatePoolsUserData: (state, action) => {
      const { field, value, sousId } = action.payload
      const index = state.data.findIndex((p) => p.sousId === sousId)

      if (index >= 0) {
        state.data[index] = { ...state.data[index], userData: { ...state.data[index].userData, [field]: value } }
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchInitialPoolsData.fulfilled, (state, action) => {
      state.data = action.payload.data
    })
  },
})

// Actions
export const { setPoolsPublicData, setPoolsUserData, setUserDataLoading, updatePoolsUserData } = PoolsSlice.actions

export default PoolsSlice.reducer
